'use client'

import { useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartData,
  type Chart,
  type ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { CloseIcon } from '@/components/ui/icons'
import type { HomeStatsTeam } from '@/lib/services/league-home'
import { getPoolAccentColor, getPoolTheme } from '@/lib/pool-theme'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

function logoProxyUrl(src: string) {
  return `/api/logo?src=${encodeURIComponent(src)}`
}

function shortTeamName(name: string | null | undefined) {
  return (name ?? '')
    .replace(/(A\.S\.D\.|ASD|SSD|Srl|SRL|U\.S\.)/g, '')
    .replace(/VOLLEY/g, 'Volley')
    .trim()
}

function splitMatchLabel(matchLabel: string | null | undefined) {
  const [home = '', away = ''] = (matchLabel ?? '').split(' vs ')
  return { home, away }
}

function teamInitials(name: string | null | undefined) {
  const cleaned = shortTeamName(name)
  if (!cleaned) return '??'
  return cleaned
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

type TooltipState = {
  open: boolean
  x: number
  y: number
  team: HomeStatsTeam | null
  point: HomeStatsTeam['progression'][number] | null
  color: string
  pinned: boolean
}

const TOOLTIP_WIDTH = 260
const TOOLTIP_OFFSET = 14
const VIEWPORT_MARGIN = 12

function formatDecimal(value: number, digits = 3) {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}

/**
 * Senza squadre in classifica non c'è nulla da disegnare.
 *
 * Il ritorno anticipato sta qui e non dentro il grafico perché gli hook non si
 * possono chiamare condizionatamente: se l'elenco passasse da vuoto a pieno fra
 * due render, l'ordine degli hook cambierebbe e React andrebbe in errore.
 */
export function HomeStatisticsSection({ leaders }: { leaders: HomeStatsTeam[] }) {
  if (leaders.length === 0) return null

  return <HomeStatisticsChart leaders={leaders} />
}

function HomeStatisticsChart({ leaders }: { leaders: HomeStatsTeam[] }) {
  const [visibleLeaderKeys, setVisibleLeaderKeys] = useState<string[]>(() => leaders.map((leader) => leader.key))
  const [tooltip, setTooltip] = useState<TooltipState>({
    open: false,
    x: 0,
    y: 0,
    team: null,
    point: null,
    color: '#2f6ac4',
    pinned: false,
  })
  const chartRef = useRef<Chart<'line'> | null>(null)
  const visibleLeaders = leaders.filter((leader) => visibleLeaderKeys.includes(leader.key))

  const labels = Array.from(
    new Map(
      visibleLeaders
        .flatMap((leader) => leader.progression.map((point) => [point.matchday, point.label] as const))
        .sort((left, right) => left[0] - right[0])
    ).values()
  )

  const datasetColors = useMemo(
    () => visibleLeaders.map((leader) => getPoolAccentColor(leader.poolSlug) ?? '#2f6ac4'),
    [visibleLeaders]
  )

  const data: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: 'Spartiacque',
        data: labels.map(() => 1),
        borderColor: 'rgba(107, 122, 144, 0.6)',
        borderWidth: 1.5,
        borderDash: [6, 6],
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        fill: false,
        tension: 0,
      },
      ...visibleLeaders.map((leader) => {
        const accent = getPoolAccentColor(leader.poolSlug) ?? '#2f6ac4'
        return {
          label: leader.team,
          data: labels.map((label) => leader.progression.find((point) => point.label === label)?.ratio ?? null),
          borderColor: accent,
          backgroundColor: `${accent}22`,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointHitRadius: 14,
          spanGaps: true,
          tension: 0.35,
        }
      }),
    ],
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7a90', font: { size: 11 } },
      },
      y: {
        beginAtZero: false,
        grid: { color: 'rgba(107,122,144,0.14)' },
        ticks: {
          color: '#6b7a90',
          callback: (value) => formatDecimal(Number(value), 2),
        },
      },
    },
  }

  const updateTooltipFromEvent = (event: MouseEvent<HTMLDivElement>, pinned: boolean) => {
    const chart = chartRef.current
    if (!chart) return

    const activeElements = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false
    )

    const activePoint = activeElements[0]
    if (!activePoint) {
      if (!pinned) {
        setTooltip((current) => (current.open && !current.pinned ? { ...current, open: false } : current))
      }
      return
    }

    if (activePoint.datasetIndex === 0) {
      if (!pinned) {
        setTooltip((current) => (current.open && !current.pinned ? { ...current, open: false } : current))
      }
      return
    }

    const leaderIndex = activePoint.datasetIndex - 1
    const team = visibleLeaders[leaderIndex] ?? null
    const label = labels[activePoint.index] ?? null
      const point = team?.progression.find((entry) => entry.label === label) ?? null
    if (!team || !point) {
      if (!pinned) {
        setTooltip((current) => (current.open && !current.pinned ? { ...current, open: false } : current))
      }
      return
    }

      const { left, top } = chart.canvas.getBoundingClientRect()
      const metaPoint = chart.getDatasetMeta(activePoint.datasetIndex).data[activePoint.index]
      const x = metaPoint?.x ?? 0
      const y = metaPoint?.y ?? 0
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const defaultX = left + x + TOOLTIP_OFFSET
      const flippedX = left + x - TOOLTIP_WIDTH - TOOLTIP_OFFSET
      const rawX = defaultX + TOOLTIP_WIDTH + VIEWPORT_MARGIN > viewportWidth
        ? flippedX
        : defaultX
      const rawY = top + y - 24
      const maxX = viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN
      const nextX = Math.min(Math.max(VIEWPORT_MARGIN, rawX), Math.max(VIEWPORT_MARGIN, maxX))
      const nextY = rawY < VIEWPORT_MARGIN ? top + y + TOOLTIP_OFFSET : rawY
      const clampedY = Math.min(nextY, viewportHeight - 220)
      const nextState: TooltipState = {
        open: true,
        x: nextX,
        y: Math.max(VIEWPORT_MARGIN, clampedY),
        team,
        point,
        color: datasetColors[leaderIndex] ?? '#2f6ac4',
        pinned,
      }

      setTooltip((current) => {
        if (
          current.open === nextState.open &&
          current.x === nextState.x &&
          current.y === nextState.y &&
          current.team?.key === nextState.team?.key &&
          current.point?.label === nextState.point?.label &&
          current.pinned === nextState.pinned
        ) {
          return current
        }
        return nextState
      })
  }

  const hideTooltip = () => {
    setTooltip((current) => (current.open ? { ...current, open: false, pinned: false } : current))
  }

  const toggleLeader = (leaderKey: string) => {
    setVisibleLeaderKeys((current) => {
      const isVisible = current.includes(leaderKey)
      if (isVisible) {
        hideTooltip()
        return current.filter((key) => key !== leaderKey)
      }
      return [...current, leaderKey]
    })
  }

  const tooltipMatch = tooltip.point ? splitMatchLabel(tooltip.point.matchLabel) : { home: '', away: '' }
  const homeTeamName = shortTeamName(tooltip.point?.homeTeam || tooltipMatch.home)
  const awayTeamName = shortTeamName(tooltip.point?.awayTeam || tooltipMatch.away)
  const tooltipTeamKey = shortTeamName(tooltip.team?.team).toLowerCase()
  const isTooltipTeamHome = tooltipTeamKey === shortTeamName(tooltip.point?.homeTeam).toLowerCase()

  return (
    <section className="shell pb-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="league-kicker">Statistiche</p>
          <h2 className="mt-2 font-display text-[32px] font-bold leading-none tracking-[-0.03em] text-[color:var(--league-ink)] md:text-[36px]">
            Quoziente punti in stagione
          </h2>
        </div>
        <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
          Top 4 squadre per rendimento PF/PS
        </span>
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
        <div className="league-card rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-[24px] font-bold tracking-[-0.03em] text-[color:var(--league-ink)]">
              Migliori 4
            </h3>
            <span className="text-[14px] text-[color:var(--league-muted)]">Classifica live</span>
          </div>

          <div className="mt-4 space-y-3">
            {leaders.map((leader, index) => {
              const theme = getPoolTheme(leader.poolSlug)
              const isVisible = visibleLeaderKeys.includes(leader.key)
              return (
                <button
                  key={leader.key}
                  type="button"
                  onClick={() => toggleLeader(leader.key)}
                  className={`block w-full rounded-2xl border p-4 text-left transition-all ${isVisible
                    ? 'border-[color:var(--league-line)] bg-[color:var(--league-surface-alt)] opacity-100'
                    : 'border-[color:var(--league-line)] bg-[color:var(--league-surface)] opacity-55 grayscale-[0.2]'}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-[14px] font-bold"
                      style={theme ? { backgroundColor: theme.accent, color: theme.foreground } : undefined}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        {leader.logoSrc ? (
                          <img
                            src={logoProxyUrl(leader.logoSrc)}
                            alt={leader.team}
                            loading="lazy"
                            decoding="async"
                            className="h-10 w-10 rounded-full object-contain"
                          />
                        ) : null}
                        <div className="min-w-0">
                          <div className="truncate font-display text-[20px] font-bold leading-none tracking-[-0.03em] text-[color:var(--league-ink)]">
                            {leader.team}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--league-muted)]">
                            <span>{leader.championshipTitle}</span>
                            <span className="h-1 w-1 rounded-full bg-[color:var(--league-line-strong)]" />
                            <span>{leader.poolLabel}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-end justify-between gap-3">
                        <div>
                          <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[color:var(--league-muted)]">Quoziente punti</div>
                          <div className="mt-1 text-[28px] font-bold leading-none tracking-[-0.04em] text-[color:var(--league-ink)]">
                            {formatDecimal(leader.ratio)}
                          </div>
                        </div>
                        <div className="text-right text-[12px] text-[color:var(--league-muted)]">
                          <div>PF {leader.pointsFor}</div>
                          <div>PS {leader.pointsAgainst}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="league-card rounded-2xl p-5 md:p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="font-display text-[24px] font-bold tracking-[-0.03em] text-[color:var(--league-ink)]">
                Andamento giornata per giornata
              </h3>
              <p className="mt-1 text-[14px] text-[color:var(--league-muted)]">
                Quoziente punti puntuale per ciascuna giornata giocata.
              </p>
            </div>
          </div>
          <div
            className="mt-5 h-[520px]"
            onMouseMove={(event) => {
              if (!tooltip.pinned) updateTooltipFromEvent(event, false)
            }}
            onClick={(event) => updateTooltipFromEvent(event, true)}
            onMouseLeave={() => {
              if (!tooltip.pinned) hideTooltip()
            }}
          >
            {visibleLeaders.length > 0 ? (
              <Line ref={chartRef} data={data} options={options} />
            ) : (
              <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-[color:var(--league-line)] text-[16px] text-[color:var(--league-muted)]">
                Attiva almeno una squadra dalle card "Migliori 4".
              </div>
            )}
          </div>
        </div>
      </div>

      {tooltip.open && tooltip.team && tooltip.point ? (
        <div
          className={`fixed z-[var(--z-dropdown)] w-[260px] overflow-hidden rounded-2xl border border-[color:var(--league-line)] bg-[color:var(--league-surface)] shadow-[var(--league-shadow-md)] ${tooltip.pinned ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className="relative px-4 py-3 text-white" style={{ backgroundColor: tooltip.color }}>
            {tooltip.pinned ? (
              <button
                type="button"
                onClick={hideTooltip}
                className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                aria-label="Chiudi tooltip"
              >
                <CloseIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}
            <div className="pr-10">
              <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-white/80">
                {tooltip.point.label} · {tooltip.team.poolLabel}
              </div>
              <div className="mt-1 font-display text-[20px] font-bold leading-none">
                {tooltip.team.team}
              </div>
            </div>
            <div className="mt-2 text-[24px] font-bold leading-none tracking-[-0.04em]">
              {formatDecimal(tooltip.point.ratio)}
            </div>
          </div>

          <div className="px-4 py-3">
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">
              Match
            </div>
            <div className="mt-2 rounded-xl bg-[color:var(--league-surface-alt)] px-3 py-3">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <div className="min-w-0 text-center">
                    {tooltip.point.homeLogoSrc ? (
                      <img
                        src={logoProxyUrl(tooltip.point.homeLogoSrc)}
                        alt={tooltip.point.homeTeam}
                        loading="lazy"
                        decoding="async"
                        className="mx-auto h-9 w-9 rounded-full object-contain"
                      />
                    ) : (
                      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--league-line)] text-[12px] font-bold text-[color:var(--league-ink)]">
                        {teamInitials(tooltip.point.homeTeam || tooltipMatch.home)}
                      </div>
                    )}
                    <div className="mt-2 truncate text-[12px] font-semibold text-[color:var(--league-ink)]">
                      {homeTeamName || 'Squadra casa'}
                    </div>
                  </div>

                <div className="text-center">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">
                    Risultato
                  </div>
                  <div className="mt-1 text-[20px] font-bold leading-none text-[color:var(--league-ink)]">
                    {tooltip.point.result ?? 'n.d.'}
                  </div>
                  <div className="mt-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">
                    vs
                  </div>
                </div>

                  <div className="min-w-0 text-center">
                    {tooltip.point.awayLogoSrc ? (
                      <img
                        src={logoProxyUrl(tooltip.point.awayLogoSrc)}
                        alt={tooltip.point.awayTeam}
                        loading="lazy"
                        decoding="async"
                        className="mx-auto h-9 w-9 rounded-full object-contain"
                      />
                    ) : (
                      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--league-line)] text-[12px] font-bold text-[color:var(--league-ink)]">
                        {teamInitials(tooltip.point.awayTeam || tooltipMatch.away)}
                      </div>
                    )}
                    <div className="mt-2 truncate text-[12px] font-semibold text-[color:var(--league-ink)]">
                      {awayTeamName || 'Squadra ospite'}
                    </div>
                  </div>
              </div>

              <div className="mt-3 border-t border-[color:var(--league-line)] pt-2.5">
                <div className="text-center text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">
                  Parziali
                </div>
                <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                  {(tooltip.point.sets?.split(' ') ?? ['n.d.']).map((setScore, index) => {
                    const parsed = setScore.match(/^(\d{1,2})-(\d{1,2})$/)
                    const homePoints = parsed ? Number(parsed[1]) : null
                    const awayPoints = parsed ? Number(parsed[2]) : null
                    const tooltipTeamWonSet = homePoints != null && awayPoints != null
                      ? (isTooltipTeamHome ? homePoints > awayPoints : awayPoints > homePoints)
                      : false

                    return (
                      <span
                        key={`${setScore}-${index}`}
                        className="rounded-full px-2.5 py-1 text-[12px] font-semibold shadow-sm"
                        style={tooltipTeamWonSet ? {
                          backgroundColor: tooltip.color,
                          color: '#ffffff',
                        } : {
                          backgroundColor: '#ffffff',
                          color: 'var(--league-ink)',
                        }}
                      >
                        {setScore}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
